import '../../styles/auth.css';
import {
  loadUser,
  logout,
  resendVerification,
  billingPortal,
  exportMyData,
  deleteMyAccount,
  type User,
} from '../../auth';
import { bootShell, showError, showSuccess, clearMessages, describeError, toast } from './_shell';

bootShell();

const loadingEl = document.getElementById('account-loading')!;
const contentEl = document.getElementById('account-content')!;
const verifyBanner = document.getElementById('verify-banner')!;
const resendBannerBtn = document.getElementById('resend-banner-btn') as HTMLButtonElement;
const resendBtn = document.getElementById('resend-btn') as HTMLButtonElement;
const manageBtn = document.getElementById('manage-btn') as HTMLButtonElement;
const logoutBtn = document.getElementById('logout-btn') as HTMLButtonElement;

const rowEmail = document.getElementById('row-email')!;
const rowName = document.getElementById('row-name')!;
const rowVerified = document.getElementById('row-verified')!;
const rowTier = document.getElementById('row-tier')!;
const rowStatus = document.getElementById('row-status')!;
const rowRenewsWrap = document.getElementById('row-renews-wrap')!;
const rowRenewsLabel = document.getElementById('row-renews-label')!;
const rowRenews = document.getElementById('row-renews')!;

function statusPill(value: string, kind: 'ok' | 'warn' | 'muted' = 'muted'): string {
  const cls = kind === 'ok' ? '' : (kind === 'warn' ? ' warn' : ' muted');
  return `<span class="pill${cls}">${value}</span>`;
}

function pretty(s: string | null | undefined, fallback = '—'): string {
  if (!s) return fallback;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function renewLabelForStatus(status: User['subscription_status']): string {
  switch (status) {
    case 'trial': return 'Trial ends';
    case 'canceled': return 'Access until';
    case 'past_due': return 'Past due since';
    case 'active': return 'Renews';
    default: return 'Active until';
  }
}

function render(user: User) {
  rowEmail.textContent = user.email;
  rowName.textContent = user.display_name || '—';

  rowVerified.innerHTML = user.email_verified
    ? statusPill('Verified', 'ok')
    : statusPill('Unverified', 'warn');

  rowTier.innerHTML = statusPill(pretty(user.subscription_tier), user.subscription_tier === 'free' ? 'muted' : 'ok');

  if (user.subscription_status === 'none') {
    rowStatus.innerHTML = statusPill('No subscription', 'muted');
  } else if (user.subscription_status === 'trial') {
    rowStatus.innerHTML = statusPill('Trial', 'ok');
  } else if (user.subscription_status === 'active') {
    rowStatus.innerHTML = statusPill('Active', 'ok');
  } else if (user.subscription_status === 'past_due') {
    rowStatus.innerHTML = statusPill('Past due', 'warn');
  } else if (user.subscription_status === 'canceled') {
    rowStatus.innerHTML = statusPill('Canceled', 'warn');
  } else {
    rowStatus.innerHTML = statusPill(pretty(user.subscription_status), 'muted');
  }

  if (user.subscription_active_until) {
    rowRenewsLabel.textContent = renewLabelForStatus(user.subscription_status);
    rowRenews.textContent = formatDate(user.subscription_active_until);
    rowRenewsWrap.style.display = '';
  } else {
    rowRenewsWrap.style.display = 'none';
  }

  // Verify-email banner + resend button visibility
  if (!user.email_verified) {
    verifyBanner.classList.add('show');
    resendBtn.style.display = '';
  } else {
    verifyBanner.classList.remove('show');
    resendBtn.style.display = 'none';
  }

  // Manage subscription only useful if they have one (or hit pricing first)
  if (user.subscription_status === 'none') {
    manageBtn.style.display = 'none';
  }
}

async function handleResend(btn: HTMLButtonElement) {
  clearMessages();
  btn.disabled = true;
  try {
    const res = await resendVerification();
    if (res.already_verified) {
      toast('Email already verified.');
      // Refresh state so the banner disappears.
      const u = await loadUser();
      if (u) render(u);
    } else if (res.sent !== false) {
      showSuccess('Verification email sent. Check your inbox.');
    } else {
      toast('Email queued. Please wait a moment before retrying.');
    }
  } catch (err: any) {
    if (err?.status === 429) {
      toast('Too soon — please wait before requesting another email.');
    } else {
      showError(describeError(err, 'Could not send verification email.'));
    }
  } finally {
    btn.disabled = false;
  }
}

resendBannerBtn.addEventListener('click', () => handleResend(resendBannerBtn));
resendBtn.addEventListener('click', () => handleResend(resendBtn));

manageBtn.addEventListener('click', async () => {
  clearMessages();
  manageBtn.disabled = true;
  try {
    const { url } = await billingPortal();
    if (url) {
      location.href = url;
      return;
    }
    toast('Could not open the billing portal.');
  } catch (err: any) {
    if (err?.status === 503) {
      toast('Billing portal is not available yet. Coming soon.');
    } else {
      showError(describeError(err, 'Could not open the billing portal.'));
    }
  } finally {
    manageBtn.disabled = false;
  }
});

logoutBtn.addEventListener('click', async () => {
  logoutBtn.disabled = true;
  try {
    await logout();
  } catch {
    /* even if the server hiccups, clear local state */
  }
  location.href = '/login.html';
});

// ----- GDPR self-service (F-06) -----

const exportBtn = document.getElementById('export-data-btn') as HTMLButtonElement | null;
const deleteBtn = document.getElementById('delete-account-btn') as HTMLButtonElement | null;

exportBtn?.addEventListener('click', async () => {
  clearMessages();
  exportBtn.disabled = true;
  try {
    await exportMyData();
    toast('Export downloaded. The JSON file is in your Downloads folder.');
  } catch (err: any) {
    if (err?.status === 429) {
      toast('Too many export requests. Wait a minute and try again.');
    } else {
      showError(describeError(err, 'Could not export your data.'));
    }
  } finally {
    exportBtn.disabled = false;
  }
});

deleteBtn?.addEventListener('click', async () => {
  clearMessages();
  // Two-step confirmation: a casual click on the danger button never deletes.
  const phrase = window.prompt(
    'This will deactivate your account immediately and permanently delete your ' +
    'data after 30 days. Active subscriptions must be canceled separately in ' +
    'the billing portal — they will keep charging until you do.\n\n' +
    'Type DELETE to confirm.'
  );
  if (phrase !== 'DELETE') {
    if (phrase !== null) toast('Deletion canceled — exact text "DELETE" required.');
    return;
  }
  deleteBtn.disabled = true;
  try {
    const res = await deleteMyAccount();
    const purge = res.hard_purge_at ? new Date(res.hard_purge_at).toLocaleDateString() : 'in 30 days';
    showSuccess(
      `Account deactivated. Data will be permanently deleted on ${purge}. ` +
      'Sign in again before then if you change your mind.'
    );
    // After delete, the session cookie was cleared server-side. Send the user
    // to the home page after a short delay so they read the message.
    setTimeout(() => { location.href = '/'; }, 4000);
  } catch (err: any) {
    showError(describeError(err, 'Could not delete your account. Email support@lopettia.com.'));
    deleteBtn.disabled = false;
  }
});

async function init() {
  const user = await loadUser();
  if (!user) {
    location.href = `/login.html?next=${encodeURIComponent('/account.html')}`;
    return;
  }
  loadingEl.style.display = 'none';
  contentEl.style.display = '';
  render(user);
}

init();
